/**
 * Roles, in one place.
 *
 * The old app re-derived `isAdmin`, `isStoreAdmin`, `isCustomerService` … inline
 * in Sidebar.js, App.js and half a dozen pages, each with a slightly different
 * spelling of the same rule. Every permission question now goes through
 * `can()` or the `usePermissions()` hook, so a rule is changed once.
 */

export const ROLES = {
  SuperAdmin: 'SuperAdmin',
  Admin: 'Admin',
  StoreAdmin: 'StoreAdmin',
  StoreManager: 'StoreManager',
  CustomerService: 'CustomerService',
  CustomerServiceAdmin: 'CustomerServiceAdmin',
  CustomerSupport: 'CustomerSupport',
  CustomerSupportAdmin: 'CustomerSupportAdmin',
  Marketing: 'Marketing',
  Finance: 'Finance',
  TopAnalytics: 'TopAnalytics',
  DeliveryProvider: 'DeliveryProvider',
  Merchandiser: 'Merchandiser',
  Inventory: 'Inventory',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/** Anything outside this set cannot use the admin portal at all. */
const PORTAL_ROLES: Role[] = [
  ROLES.SuperAdmin,
  ROLES.Admin,
  ROLES.StoreAdmin,
  ROLES.StoreManager,
  ROLES.CustomerService,
  ROLES.CustomerServiceAdmin,
  ROLES.CustomerSupport,
  ROLES.CustomerSupportAdmin,
  ROLES.Marketing,
  ROLES.Finance,
  ROLES.DeliveryProvider,
];

export function canAccessPortal(roles: string[] | undefined | null): boolean {
  if (!roles?.length) return false;
  return roles.some((role) => PORTAL_ROLES.includes(role as Role));
}

/**
 * Named capabilities. Nav items and route guards reference these, never raw
 * role strings — so "who can see payouts" is answered by reading one line.
 */
export const PERMISSIONS = {
  isSuperAdmin: [ROLES.SuperAdmin],
  isAdmin: [ROLES.SuperAdmin, ROLES.Admin],
  isStoreAdmin: [ROLES.SuperAdmin, ROLES.StoreAdmin],
  isStoreManager: [ROLES.SuperAdmin, ROLES.StoreAdmin, ROLES.StoreManager],
  isCustomerService: [
    ROLES.SuperAdmin,
    ROLES.CustomerService,
    ROLES.CustomerServiceAdmin,
    ROLES.CustomerSupport,
    ROLES.CustomerSupportAdmin,
  ],
  isMarketing: [ROLES.SuperAdmin, ROLES.Marketing],
  isFinance: [ROLES.SuperAdmin, ROLES.Finance, ROLES.TopAnalytics],
  // StoreAdmin as well: the original's 3PL menu let the store's own admin in
  // beside the provider's staff.
  isDeliveryProvider: [
    ROLES.SuperAdmin,
    ROLES.StoreAdmin,
    ROLES.DeliveryProvider,
  ],
  isMerchandiser: [ROLES.SuperAdmin, ROLES.StoreAdmin, ROLES.Merchandiser],
  hasInventory: [ROLES.SuperAdmin, ROLES.StoreAdmin, ROLES.Inventory],
  /**
   * Reverse a gift-card transaction, or transfer one card's balance to another.
   *
   * Narrower than `isCustomerService` on purpose: these two are the only
   * gift-card operations the API itself role-checks, and it accepts exactly
   * SuperAdmin and CustomerSupportAdmin — `GiftCardService.RemoveGiftCardRedemptionAsync`
   * and `TransferGiftCardBalancesAsync` answer "not enough privileges" to
   * everyone else. Gating the buttons on the same two names is what keeps a
   * Finance user from being shown an action that can only fail.
   */
  canReverseGiftCards: [ROLES.SuperAdmin, ROLES.CustomerSupportAdmin],
  /**
   * Cancel someone else's booking.
   *
   * The roles `EventsService.CancelBookingAsync` (BookingApi) accepts, less the
   * `Owner` role the portal never sees. Finance is absent there, so it is
   * absent here too, even though Finance may record and void payments.
   */
  canCancelBookings: [
    ROLES.SuperAdmin,
    ROLES.Admin,
    ROLES.StoreAdmin,
    ROLES.StoreManager,
    ROLES.CustomerSupport,
    ROLES.CustomerSupportAdmin,
  ],
  /**
   * Read the POS discount usage report.
   *
   * Named after what `GetDiscountsOnOrdersReportAsync` actually accepts —
   * Finance, StoreAdmin or SuperAdmin — rather than reusing `isFinance`, which
   * includes TopAnalytics and would put a menu row in front of an account the
   * API refuses. Marketing is deliberately absent: the people who *define* the
   * discounts are not the people allowed to read what they cost.
   */
  viewDiscountReport: [ROLES.SuperAdmin, ROLES.Finance, ROLES.StoreAdmin],
  /**
   * Void or refund a gift voucher.
   *
   * What `PermissionExtensions.HasFinancialPermissionsAsync` (BookingApi)
   * accepts, less the `Owner` role the portal never sees. Narrower than
   * `isFinance`, which the API does not accept for vouchers — offering Finance
   * the buttons would only show an action that fails.
   */
  canRefundVouchers: [ROLES.SuperAdmin, ROLES.Admin],
  /**
   * Read page visits across accounts.
   *
   * The roles `PageVisitAnalyticsService` accepts, less the `Owner` role the
   * portal never sees. Customer service is deliberately absent: an agent looks
   * at one caller's history from the customer tab, and is not handed
   * everybody's.
   */
  viewUsageAnalytics: [
    ROLES.SuperAdmin,
    ROLES.Admin,
    ROLES.Marketing,
    ROLES.TopAnalytics,
  ],
  viewGlobalDashboard: [ROLES.SuperAdmin, ROLES.TopAnalytics],
  viewStoreDashboard: [
    ROLES.SuperAdmin,
    ROLES.StoreAdmin,
    ROLES.Finance,
    ROLES.TopAnalytics,
  ],
} as const;

export type Permission = keyof typeof PERMISSIONS;

export function can(
  roles: string[] | undefined | null,
  permission: Permission
): boolean {
  if (!roles?.length) return false;
  const allowed: readonly string[] = PERMISSIONS[permission];
  return roles.some((role) => allowed.includes(role));
}
